<template>
  <div>
    <div class="ma-4 px-4">
      <v-sheet outlined>
        <v-data-table
          :headers="settlementHeaders"
          :items="settlements"
          fixed-header
          :items-per-page="5"
          hide-default-footer
          class="secondary row-height-50"
        >
          <template #[`item.date`]="{ value }">
            {{ formatDate(value, false) }}
          </template>
        </v-data-table>
      </v-sheet>
      <v-row class="pt-6 d-flex justify-between">
        <div class="d-flex align-center mx-3">
          <span class="text-5xl font-weight-bold accent--text">
            Settled Transactions | Collected Amount -
            {{ settlements[0]?.total_gross_amount }} | Settled Amount -
            {{ settlements[0]?.settled_amount }}
          </span>
        </div>
        <v-spacer></v-spacer>
        <div class="d-flex ma-2">
          <v-btn
            class="success white--text ml-3"
            :disabled="!selectedItems.length || !settlementTransactions.length"
            @click="confirmationDialog = true"
          >
            Verify
          </v-btn>
        </div>
      </v-row>
      <v-row class="mt-3">
        <v-col cols="12">
          <v-sheet outlined>
            <v-data-table
              v-model="selectedItems"
              :headers="settlementTransactionHeaders"
              :items="filteredSettlementTransactions"
              fixed-header
              class="secondary row-height-40"
              :show-select="true"
              :show-select-all="true"
              item-key="pgi_ref_number"
            >
              <template #[`item.tx_date`]="{ value }">
                {{ formatDates(value, true) }}
              </template>
              <template #[`item.batch`]="{ item }">
                {{ item.year }}-{{ item.batch }}
              </template>
              <template #[`item.settlement_date`]="{ value }">
                {{ formatDates(value, true) }}
              </template>
            </v-data-table>
          </v-sheet>
        </v-col>
      </v-row>
    </div>
    <v-dialog v-model="confirmationDialog" width="500" persistent>
      <confirmationDialog
        v-if="confirmationDialog"
        :confirmationText="`Are you sure you want to confirm receipt of the payment${
          selectedItems.length > 1 ? 's' : ''
        }?`"
        @closeDialog="confirmationDialog = false"
        @saveDialog="updatePaymentStatus()"
      />
    </v-dialog>
  </div>
</template>

<script>
import {
  settlementHeaders,
  settlementTransactionHeaders,
} from "@/constants/headers";
import confirmationDialog from "@/components/confirmationDialog.vue";
export default {
  components: { confirmationDialog },
  data() {
    return {
      settlementHeaders: settlementHeaders,
      settlementTransactionHeaders: settlementTransactionHeaders,
      settlements: [],
      settlementTransactions: [],
      currentSettlementPV: null,
      selectedItems: [],
      confirmationDialog: false,
    };
  },
  watch: {},
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    filteredSettlementTransactions() {
      return this.settlementTransactions.filter((a) => a.finance_check == "N");
    },
  },
  async created() {
    this.currentSettlementPV = this.$route.params.id;
    this.$store.commit("setBreadcrumbs", [
      { text: "Settlements", to: { name: "Settlements" } },
      { text: "Transactions" },
    ]);

    this.initialise();
    if (this.settlementHeaders)
      this.settlementHeaders = this.settlementHeaders.slice(0, -1);
  },
  methods: {
    async initialise() {
      await this.getSettlements();
    },
    async getSettlements() {
      await this.$axios
        .get("/verify_settlement_by_pv_number/" + this.currentSettlementPV)
        .then((res) => {
          this.settlements = [res.data.data];
          this.settlementTransactions =
            this.settlements[0].settled_transactions;
        })
        .catch(async (error) => {
          this.$store.commit("showSnackbar", {
            message: error.response.data.message || "No Data Found",
            color: "error",
          });
        });
    },
    formatDate(dateString, needHour) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      const hour = date.getHours().toString().padStart(2, "0");
      const minute = date.getMinutes().toString().padStart(2, "0");
      return needHour
        ? `${month}-${day}-${year} ${hour}:${minute}`
        : `${day}-${month}-${year}`;
    },
    formatDates(dateString, needHour) {
      if (!dateString) return "-";

      const [datePart, timePart] = dateString.split(" ");
      const [day, month, year] = datePart.split("/").map(Number);
      const [hour = 0, minute = 0, second = 0] = (timePart || "00:00:00")
        .split(":")
        .map(Number);

      const date = new Date(year, month - 1, day, hour, minute, second);

      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();
      const hh = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");

      return needHour
        ? `${dd}-${mm}-${yyyy} ${hh}:${min}`
        : `${dd}-${mm}-${yyyy}`;
    },
    async updatePaymentStatus() {
      const selectedIds = new Set(
        this.selectedItems.map((item) => String(item.payment_id || item.id))
      );

      const preSelectedItemIds = this.filteredSettlementTransactions
        .filter(
          (a) =>
            a.status == "pre_application" &&
            selectedIds.has(String(a.payment_id || a.id))
        )
        .map((item) => item.payment_id || item.id);

      const studentSelectedItemIds = this.filteredSettlementTransactions
        .filter(
          (a) =>
            a.status == "student_admit_plan" &&
            selectedIds.has(String(a.payment_id || a.id))
        )
        .map((item) => item.payment_id || item.id);

      const invoiceSelectedItemIds = this.filteredSettlementTransactions
        .filter(
          (a) =>
            a.status == "student_invoice" &&
            selectedIds.has(String(a.payment_id || a.id))
        )
        .map((item) => item.payment_id || item.id);

      preSelectedItemIds.length > 0 &&
        (await this.preApplicationPaymentFinanceCheck(preSelectedItemIds));
      studentSelectedItemIds.length > 0 &&
        (await this.studentPlanPaymentsFinanceCheck(studentSelectedItemIds));
      invoiceSelectedItemIds.length > 0 &&
        (await this.studentInvoicePaymentsCheck(invoiceSelectedItemIds));
    },
    async preApplicationPaymentFinanceCheck(ids) {
      await this.$axios
        .put(`/update_payment_by_cfo/`, {
          ids: ids,
          filter_by: "pre_application",
        })
        .then(() => {
          this.confirmationDialog = false;
          this.initialise();
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async studentPlanPaymentsFinanceCheck(ids) {
      await this.$axios
        .put(`/update_payment_by_cfo/`, {
          ids: ids,
          filter_by: "application",
        })
        .then(() => {
          this.confirmationDialog = false;
          this.initialise();
        })
        .catch((error) => {
          console.log(error);
        });
    },
    async studentInvoicePaymentsCheck(ids) {
      await this.$axios
        .put(`/update_payment_by_cfo/`, {
          ids: ids,
          filter_by: "student_invoice",
        })
        .then(() => {
          this.confirmationDialog = false;
          this.initialise();
        })
        .catch((error) => {
          console.log(error);
        });
    },
  },
};
</script>
